/**
 * Compressao do texto claro, antes de cifrar.
 *
 * Fica no app e nao no crypto-core porque `CompressionStream` e API de
 * plataforma, e aquele pacote nao depende de nenhuma — a mesma razao pela qual
 * o cofre recebe a frase em bytes e nao em string.
 *
 * ── A TROCA, sem enfeite ────────────────────────────────────────────────────
 *
 * Comprimir antes de cifrar faz o TAMANHO do que sai depender de QUANTO o
 * texto comprime, e isso e uma pista sobre o conteudo que o texto cifrado
 * sozinho nao daria. Texto repetitivo encolhe muito; texto ja denso, quase
 * nada. Quem observa o fio ve a diferenca.
 *
 * O que torna isso aceitavel aqui, e nao no caso classico (CRIME, BREACH): la
 * o atacante INJETA texto escolhido no mesmo fluxo comprimido que o segredo, e
 * mede o resultado repetidas vezes. Aqui cada mensagem e comprimida sozinha,
 * com conteudo que so quem escreve controla, e nao ha repeticao sob demanda.
 * O comprimento ja vazava antes; a compressao muda a natureza do vazamento,
 * nao o cria.
 *
 * Signal nao comprime. A diferenca de contexto e que la o transporte e do
 * proprio Signal, e aqui a mensagem viaja colada a mao num campo de texto de
 * mensageiro — cabe, ou nao cabe.
 *
 * Quem nao quiser essa troca desliga: `comprimir()` respeita a preferencia, e
 * sem o bit `Compressed` o outro lado le o texto direto.
 */
import { MessageFlag } from '@criptohomer/crypto-core'

/**
 * Nao comprime abaixo disto.
 *
 * Deflate tem cabecalho e arvore: em texto curto a saida CRESCE. Medido neste
 * projeto — 2 bytes viram 6. Abaixo deste limite nem vale tentar.
 */
export const MINIMO_PARA_COMPRIMIR = 180

const suportado = typeof CompressionStream === 'function'

async function passarPor(fluxo: ReadableStream<Uint8Array<ArrayBufferLike>>): Promise<Uint8Array> {
  const partes: Uint8Array[] = []
  const leitor = fluxo.getReader()
  for (;;) {
    const { done, value } = await leitor.read()
    if (done) break
    partes.push(value)
  }
  const total = partes.reduce((n, p) => n + p.length, 0)
  const saida = new Uint8Array(total)
  let em = 0
  for (const p of partes) {
    saida.set(p, em)
    em += p.length
  }
  return saida
}

/**
 * `BufferSource` porque e o que os fluxos de (des)compressao aceitam.
 *
 * O `Uint8Array.from` nao e desperdicio: a entrada pode estar apoiada num
 * `SharedArrayBuffer`, que `BufferSource` recusa, e copiar tambem garante que
 * ninguem altere os bytes enquanto o fluxo os consome.
 */
const daBytes = (bytes: Uint8Array): ReadableStream<BufferSource> =>
  new ReadableStream({
    start(c) {
      c.enqueue(Uint8Array.from(bytes))
      c.close()
    },
  })

export interface Comprimido {
  readonly bytes: Uint8Array
  /** Flags a somar no quadro. Zero quando nao comprimiu. */
  readonly flags: number
}

/**
 * Comprime se valer a pena. Devolve o original quando nao vale — e a decisao
 * vai no bit, nao na esperanca de que o outro lado adivinhe.
 */
export async function comprimir(claro: Uint8Array, ligado = true): Promise<Comprimido> {
  if (!ligado || !suportado || claro.length < MINIMO_PARA_COMPRIMIR) {
    return { bytes: claro, flags: 0 }
  }
  try {
    const comprimido = await passarPor(
      daBytes(claro).pipeThrough(new CompressionStream('deflate-raw')),
    )
    // Comprimir e uma aposta, nao uma certeza: texto ja denso pode crescer.
    // Se cresceu, manda o original — o bit e que diz qual dos dois foi.
    return comprimido.length < claro.length
      ? { bytes: comprimido, flags: MessageFlag.Compressed }
      : { bytes: claro, flags: 0 }
  } catch {
    // Compressao falhar nao pode impedir alguem de mandar mensagem.
    return { bytes: claro, flags: 0 }
  }
}

/** Desfaz, se o bit disser que houve compressao. */
export async function descomprimir(bytes: Uint8Array, flags: number): Promise<Uint8Array> {
  if ((flags & MessageFlag.Compressed) === 0) return bytes
  if (typeof DecompressionStream !== 'function') {
    throw new Error('esta mensagem veio comprimida e este navegador não sabe descomprimir')
  }
  return await passarPor(daBytes(bytes).pipeThrough(new DecompressionStream('deflate-raw')))
}
